import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { LOCAL_AUTH_FAILURE } from '../../../core/interceptors/local-auth-failure';
import { MemberProfile, ProfileCommand, isRecord, parseMemberProfile, profileUpdateBody } from '../domain/member-profile';

export class ProfileRequestError extends Error {
  constructor(readonly reason: 'authentication' | 'forbidden' | 'unavailable' | 'invalid-response' | 'rejected') {
    super(reason);
  }
}

@Injectable({ providedIn: 'root' })
export class MemberProfileApi {
  private readonly http = inject(HttpClient);
  private options() { return { context: new HttpContext().set(LOCAL_AUTH_FAILURE, true) }; }

  private async request(path: string, body?: unknown): Promise<Record<string, unknown>> {
    try {
      const url = `${environment.apiBaseUrl}${path}`;
      const response = await firstValueFrom((body === undefined ? this.http.get<unknown>(url, this.options()) :
        this.http.post<unknown>(url, body, this.options())).pipe(timeout(30000)));
      if (!isRecord(response) || typeof response['success'] !== 'boolean') throw new ProfileRequestError('invalid-response');
      if (response['success'] !== true) throw new ProfileRequestError('rejected');
      return response;
    } catch (error) {
      if (error instanceof ProfileRequestError) throw error;
      if (error instanceof HttpErrorResponse && error.status === 401) throw new ProfileRequestError('authentication');
      if (error instanceof HttpErrorResponse && error.status === 403) throw new ProfileRequestError('forbidden');
      throw new ProfileRequestError('unavailable');
    }
  }

  async read(): Promise<MemberProfile> {
    const profile = parseMemberProfile((await this.request('/member/self'))['result']);
    if (!profile) throw new ProfileRequestError('invalid-response');
    return profile;
  }

  async exists(name: 'email' | 'loginId', value: string): Promise<boolean> {
    const result = (await this.request('/member/exists', { name, value }))['result'];
    if (typeof result !== 'boolean') throw new ProfileRequestError('invalid-response');
    return result;
  }

  async checkPassword(loginId: string, password: string): Promise<boolean> {
    try { await this.request('/member/auth', { loginId, password }); return true; }
    catch (error) {
      if (error instanceof ProfileRequestError && error.reason === 'rejected') return false;
      throw error;
    }
  }

  async update(id: number, command: ProfileCommand): Promise<void> {
    // API acknowledgement, not its database-specific upsert tuple, is the write contract.
    await this.request('/member', profileUpdateBody(id, command));
  }
}
